/**
 * Message service for handling messaging functionality
 */
import api from './api';
import { User } from './userService';

// Interface for a single message
export interface Message {
    id: number;
    conversationId: number;
    senderId: number;
    content: string;
    createdAt: string; // ISO 8601 format
    // Add sender details if backend includes them directly
    sender?: { 
        id: number;
        name: string;
        profile_image_url?: string;
    };
}

// Interface for a conversation list item
export interface ConversationListItem {
    id: number; // Conversation ID
    participants: Partial<User>[]; // Array of participants (User info)
    lastMessage: { // Snippet of the last message
        content: string;
        createdAt: string;
        senderId: number;
    } | null;
    unreadCount?: number; // Optional: if backend tracks this
    updatedAt: string; // Timestamp of the last activity
}

// Interface for sending a new message (payload)
interface SendMessagePayload {
    conversationId: number;
    content: string;
}

/**
 * Fetch the list of conversations for the current user
 */
export const getConversations = async (): Promise<ConversationListItem[]> => {
    try {
        const response = await api.get('/conversations');
        if (response.data.success && Array.isArray(response.data.data)) {
            return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch conversations');
    } catch (error: any) {
        console.error("Get conversations service error:", error);
        throw new Error(error.response?.data?.message || 'Failed to fetch conversations');
    }
};

/**
 * Fetch messages for a specific conversation
 * TODO: Add pagination parameters (e.g., limit, beforeCursor)
 */
export const getMessages = async (conversationId: number): Promise<Message[]> => {
    try {
        const response = await api.get(`/conversations/${conversationId}/messages`);
        if (response.data.success && Array.isArray(response.data.data)) {
            return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch messages');
    } catch (error: any) {
        console.error(`Get messages (convId: ${conversationId}) service error:`, error);
        throw new Error(error.response?.data?.message || 'Failed to fetch messages');
    }
};

/**
 * Send a new message (via API - maybe fallback or initial message?)
 * NOTE: Primary sending mechanism should be via WebSocket (socketService.emit)
 */
export const sendMessageAPI = async (payload: SendMessagePayload): Promise<{ success: boolean; message: Message }> => {
    try {
        const response = await api.post(`/conversations/${payload.conversationId}/messages`, { content: payload.content });
        if (response.data.success && response.data.message) {
            return { success: true, message: response.data.message };
        }
        throw new Error(response.data.message || 'Failed to send message via API');
    } catch (error: any) {
        console.error(`Send message API (convId: ${payload.conversationId}) service error:`, error);
        throw new Error(error.response?.data?.message || 'Failed to send message via API');
    }
};

/**
 * Mark messages as read
 */
export const markAsRead = async (messageIds: number[]): Promise<{ success: boolean }> => {
  const response = await api.post('/v1/messages/mark-read', { message_ids: messageIds });
  return { success: response.data.success };
};

/**
 * Delete a message
 */
export const deleteMessage = async (messageId: number): Promise<{ success: boolean }> => {
  const response = await api.delete(`/v1/messages/${messageId}`);
  return { success: response.data.success };
};

/**
 * Get unread message count
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get('/v1/messages/unread-count');
  if (response.data.success) {
    return response.data.data.count;
  }
  return 0;
};

/**
 * Find an existing 1-on-1 conversation with a target user, 
 * or create a new one if none exists.
 * Returns the ID of the existing or newly created conversation.
 */
export const findOrCreateConversation = async (targetUserId: number): Promise<number | null> => {
    try {
        const response = await api.post('/conversations/findOrCreate', { targetUserId });
        if (response.data.success && response.data.conversationId) {
            return response.data.conversationId as number;
        }
        // Throw error if success is false or conversationId is missing
        throw new Error(response.data.message || 'Failed to find or create conversation');
    } catch (error: any) {
        console.error(`Find/Create Conversation (targetUserId: ${targetUserId}) service error:`, error);
        // Re-throw a more specific error message if available from backend
        throw new Error(error.response?.data?.message || 'Failed to find or create conversation');
    }
};
